// apps/web/src/pages/settings/DayExceptionDialog.tsx — Phase 2 plan 02-04 B6·B day-exception modal.
//
// Modal opened via /admin/einstellungen/oeffnungszeiten/ausnahme.
//   - No `?id=`: Create mode (existing behavior). Empty form, mutable
//     date, "Ausnahme hinzufügen" title.
//   - `?id=dx-1` with matching `{ dayException }` Location-State: Edit
//     mode. Form fields hydrate from the typed payload; the date is
//     locked so the date-keyed upsert contract (260723-pqo scope 2)
//     remains stable — the server resolves the row by (date, restaurantId).
//   - `?id=` with no matching state OR a state carrying a different id:
//     safe "load error" state — `exceptionLoadError` rendered with
//     role=alert, Save disabled, Cancel still navigates back.
//
// On save → upsertDayException mutation + invalidate dayExceptions
// cache + close the dialog (navigate back to opening hours page).
//
// zod-equivalent: status === "closed" disables the time fields; status
// === "custom_range" requires both times. Mirrored client-side so the
// user gets immediate feedback.

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gqlRequest } from "../../lib/api";
import { UPSERT_DAY_EXCEPTION_MUTATION } from "./operations";

interface UpsertResponse {
  upsertDayException: {
    id: string;
    date: string;
    status: "CLOSED" | "CUSTOM_RANGE";
  };
}

// Shape carried by react-router Location-State when the row's Edit
// button is clicked. Optional fields arrive nullable from the server
// (CLOSED exceptions have null times).
interface DayExceptionEditPayload {
  id: string;
  date: string;
  status: "CLOSED" | "CUSTOM_RANGE";
  openTime: string | null;
  closeTime: string | null;
  closeNextDay: boolean | null;
}

// Location-State container used by the Edit control on the opening-
// hours page. Defensive: an unknown shape is treated as "no state".
interface DayExceptionLocationState {
  dayException?: DayExceptionEditPayload;
}

export function DayExceptionDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // 260723-pqo scope 3: ?id= is the primary hint; Location-State is
  // the typed payload. Both must agree on the id before we hydrate
  // — otherwise we render the load-error state.
  const editId = searchParams.get("id");
  const state = location.state as DayExceptionLocationState | null;
  const editRecord =
    editId !== null && state?.dayException?.id === editId
      ? state.dayException
      : null;

  // Lazy initial state so the form is correct on the very first render
  // — no effect-driven sync, no flash of empty fields in edit mode.
  const initial = useMemo(
    () => ({
      date: editRecord?.date ?? "",
      status: editRecord?.status ?? ("CLOSED" as const),
      openTime: editRecord?.openTime ?? "",
      closeTime: editRecord?.closeTime ?? "",
      closeNextDay: editRecord?.closeNextDay ?? false,
    }),
    [editRecord],
  );

  const [date, setDate] = useState(initial.date);
  const [status, setStatus] = useState<"CLOSED" | "CUSTOM_RANGE">(
    initial.status,
  );
  const [openTime, setOpenTime] = useState(initial.openTime);
  const [closeTime, setCloseTime] = useState(initial.closeTime);
  const [closeNextDay, setCloseNextDay] = useState(initial.closeNextDay);

  // Saving is impossible when:
  //   - date is empty (Create needs a date; Edit would lose the row key)
  //   - status is CUSTOM_RANGE and either time is missing
  //   - we are in a broken-id edit state (no row to update)
  const loadError = editId !== null && editRecord === null;
  const canSave =
    !loadError &&
    date.length > 0 &&
    (status === "CLOSED" || (openTime.length > 0 && closeTime.length > 0));

  const mutation = useMutation({
    mutationFn: async () => {
      const input: Record<string, unknown> = { date, status };
      if (status === "CUSTOM_RANGE") {
        input.openTime = openTime;
        input.closeTime = closeTime;
        input.closeNextDay = closeNextDay;
      }
      // The upsert is keyed by (date, restaurantId) — `id` is NOT part
      // of the input. Edit + Create share the same mutation; the row
      // is found by the date the form was hydrated with.
      return gqlRequest<UpsertResponse>(UPSERT_DAY_EXCEPTION_MUTATION, {
        input,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["settings", "dayExceptions"],
      });
      navigate("/admin/einstellungen/oeffnungszeiten");
    },
  });

  const isEdit = editRecord !== null;

  return createPortal(
    <div
      className="fixed inset-0 z-20 bg-black/40 flex items-center justify-center px-4"
      data-testid="exception-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={
        isEdit
          ? t("settings.hours.exceptionEditTitle")
          : t("settings.hours.exceptionTitle")
      }
    >
      <div className="dialog bg-bg rounded-md shadow-lg w-full max-w-md p-6 space-y-4">
        <h2
          className="font-heading text-xl"
          data-testid="exception-dialog-title"
        >
          {isEdit
            ? t("settings.hours.exceptionEditTitle")
            : t("settings.hours.exceptionTitle")}
        </h2>

        <label className="block">
          <span className="block text-sm font-medium text-text/70 mb-1">
            {t("settings.hours.exceptionDate")}
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="field w-full disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="exception-date"
            // Date is the upsert key — must not change in edit mode.
            disabled={isEdit}
            required
          />
        </label>

        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-text/70 mb-1">
            Status
          </legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="CLOSED"
              checked={status === "CLOSED"}
              onChange={() => setStatus("CLOSED")}
              data-testid="exception-status-closed"
            />
            <span>{t("settings.hours.exceptionStatusClosed")}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="CUSTOM_RANGE"
              checked={status === "CUSTOM_RANGE"}
              onChange={() => setStatus("CUSTOM_RANGE")}
              data-testid="exception-status-custom"
            />
            <span>{t("settings.hours.exceptionStatusCustom")}</span>
          </label>
        </fieldset>

        {status === "CUSTOM_RANGE" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-sm font-medium text-text/70 mb-1">
                  {t("settings.hours.exceptionOpen")}
                </span>
                <input
                  type="time"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  className="field w-full"
                  data-testid="exception-open"
                  required
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-text/70 mb-1">
                  {t("settings.hours.exceptionClose")}
                </span>
                <input
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  className="field w-full"
                  data-testid="exception-close"
                  required
                />
              </label>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={closeNextDay}
                onChange={(e) => setCloseNextDay(e.target.checked)}
                data-testid="exception-close-next-day"
              />
              <span>{t("settings.hours.exceptionCloseNextDay")}</span>
            </label>
          </>
        )}

        {loadError && (
          <p
            className="text-sm text-red-700"
            data-testid="exception-load-error"
            role="alert"
          >
            {t("settings.hours.exceptionLoadError")}
          </p>
        )}

        {mutation.isError && (
          <p
            className="text-sm text-red-700"
            data-testid="exception-error"
            role="alert"
          >
            {t("settings.hours.saveError")}
          </p>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/einstellungen/oeffnungszeiten")}
            data-testid="exception-cancel"
          >
            {t("settings.hours.exceptionCancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSave || mutation.isPending}
            onClick={() => mutation.mutate()}
            data-testid="exception-save"
          >
            {t("settings.hours.exceptionSave")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
